import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Tooltip from 'react/components/common/Tooltip/Tooltip';
import Icon from 'react/components/common/Icon/Icon';
import { formatNumber } from 'react/components/apps/audience/AudienceExplorer/AudienceInsightsWidget/QuickTable/utils/tableFormatters';
import {
  getCategoryDisplayName,
  getFullTaxonomyPath,
} from 'react/components/apps/audience/AudienceExplorer/AudienceInsightsWidget/QuickTable/utils/categoryGrouping';
import { pickCategoryPathLabel } from 'react/components/apps/audience/AudienceExplorer/AudienceInsightsWidget/QuickTable/utils/categoryPathDisplay';

const DEFAULT_FONT = '13px sans-serif';
const HIERARCHY_INDENT_BASE_PX = 12;
const HIERARCHY_INDENT_STEP_PX = 14;

const indentForDepth = (depth) => HIERARCHY_INDENT_BASE_PX + depth * HIERARCHY_INDENT_STEP_PX;

const splitHierarchyLevels = (taxonomyPath) =>
  taxonomyPath
    .split('>')
    .map((part) => part.trim())
    .filter(Boolean);

/** Reads the rendered width and computed font of a label, updating on resize. */
const useResponsiveLabelLayout = () => {
  const containerRef = useRef(null);
  const labelRef = useRef(null);
  const [layout, setLayout] = useState({ width: 0, font: DEFAULT_FONT });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const read = () => {
      const fontHost = labelRef.current || container;
      const cs = window.getComputedStyle(fontHost);
      const font = cs.font || `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      setLayout({ width: Math.max(0, Math.floor(container.clientWidth)), font });
    };

    const observer = new ResizeObserver(read);
    observer.observe(container);
    read();
    return () => observer.disconnect();
  }, []);

  return { containerRef, labelRef, layout };
};

const CategoryPathTooltip = ({ levels }) => (
  <div className="category-path-tooltip">
    <div className="category-path-tooltip-title">Hierarchy Path</div>
    {levels.map((level, depth) => (
      <div
        // Index is part of the identity here: same string can repeat at different depths.
        // eslint-disable-next-line react/no-array-index-key
        key={`${depth}-${level}`}
        className="category-path-tooltip-row"
        style={{ paddingLeft: indentForDepth(depth) }}
      >
        {depth > 0 ? '> ' : ''}
        {level}
      </div>
    ))}
  </div>
);

CategoryPathTooltip.propTypes = {
  levels: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/** Numeric metric cells that share the "tooltip + formatted span" structure. */
const renderMetricCell = (column, category, displayName) => {
  switch (column.id) {
    case 'interest-portion': {
      const value = category['interest-portion'] || 0;
      return (
        <Tooltip content={`${value.toFixed(2)}% of selected audience is included in ${displayName}`}>
          <span>{value.toFixed(2)}%</span>
        </Tooltip>
      );
    }

    case 'segment-portion': {
      const value = category['segment-portion'] || 0;
      return (
        <Tooltip content={`${value.toFixed(2)}% of the users in ${displayName} are in the selected audience`}>
          <span>{value.toFixed(2)}%</span>
        </Tooltip>
      );
    }

    case 'uniqueness-index': {
      const value = category['uniqueness-index'] || 0;
      return (
        <Tooltip
          content={`Users in ${displayName} are x${value.toFixed(
            2
          )} times more likely to be in the target audience than the average user`}
        >
          <span>{value.toFixed(2)}</span>
        </Tooltip>
      );
    }

    case 'reach': {
      const { reach } = category;
      if (reach === null || reach === undefined) return <span className="empty-cell" />;
      return (
        <Tooltip content={`${formatNumber(reach, 0)} users are included in ${displayName}`}>
          <span>{formatNumber(reach, 0)}</span>
        </Tooltip>
      );
    }

    case 'id':
      return <span className="dmp-category-id">{category.id || '—'}</span>;

    default:
      return null;
  }
};

/**
 * CategoryItemRow - Child row for nested taxonomy categories (paths of arbitrary depth).
 *
 * Renders the category name with a width-aware truncated taxonomy path,
 * a hierarchy tooltip, and the metric columns. Optionally shows a checkbox
 * for category selection when targeting is enabled.
 */
const CategoryItemRow = ({ category, columns, isSelected, onSelect, isCategoryTargetingVisible }) => {
  const { containerRef: pathSlotRef, labelRef: labelTextRef, layout } = useResponsiveLabelLayout();

  const displayName = useMemo(() => getCategoryDisplayName(category), [category]);
  const taxonomyPath = useMemo(() => getFullTaxonomyPath(category), [category]);
  const hierarchyLevels = useMemo(() => splitHierarchyLevels(taxonomyPath), [taxonomyPath]);

  const labelText = useMemo(() => {
    if (!category?.path?.length) return displayName;
    return pickCategoryPathLabel(category.path, layout.width, layout.font);
  }, [category, displayName, layout]);

  const tooltipContent = useMemo(
    () => (hierarchyLevels.length > 0 ? <CategoryPathTooltip levels={hierarchyLevels} /> : category.description || ''),
    [hierarchyLevels, category.description]
  );

  const handleCheckboxClick = useCallback(
    (event) => {
      event.stopPropagation();
      onSelect(category);
    },
    [category, onSelect]
  );

  const renderPhraseCell = () => (
    <div className="category-item-name-wrapper">
      {isCategoryTargetingVisible ? (
        <div
          className="col-checkbox"
          role="checkbox"
          tabIndex={0}
          aria-checked={isSelected}
          onClick={handleCheckboxClick}
        >
          <Icon
            iconId={isSelected ? 'selected-checkbox' : 'unselected-checkbox'}
            className={classNames('category-checkbox-icon', {
              'selected-checkbox-icon': isSelected,
            })}
          />
        </div>
      ) : (
        <div className="category-item-name-spacer" />
      )}
      <div className="category-phrase-path-slot" ref={pathSlotRef}>
        <Tooltip content={tooltipContent} options={{ maxWidth: 'none' }}>
          <div className="category-item-name">
            <span className="category-name-text" ref={labelTextRef}>
              {labelText}
            </span>
          </div>
        </Tooltip>
      </div>
    </div>
  );

  const renderCell = (column) =>
    column.id === 'phrase' ? renderPhraseCell() : renderMetricCell(column, category, displayName);

  return (
    <div className={classNames('category-item-row', { selected: isSelected })}>
      {columns.map((column) => (
        <div key={column.id} className={classNames('column', `col-${column.id}`, `flex${column.flex || '1'}`)}>
          {renderCell(column)}
        </div>
      ))}
    </div>
  );
};

CategoryItemRow.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    phrase: PropTypes.string,
    path: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    reach: PropTypes.number,
    'uniqueness-index': PropTypes.number,
    'interest-portion': PropTypes.number,
    'segment-portion': PropTypes.number,
  }).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      flex: PropTypes.string,
    })
  ).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  isCategoryTargetingVisible: PropTypes.bool,
};

CategoryItemRow.defaultProps = {
  isSelected: false,
  onSelect: () => {},
  isCategoryTargetingVisible: false,
};

export default React.memo(CategoryItemRow);
